import EditingLockedContent from 'Containers/EditingLockedContentContext';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { path } from 'Utility/ramdaReplacement';
import Toolbar from '../../components/Toolbar';
import { actions as actionsDocument } from '../../store/documents/actions';
import { actions as actionsForm } from '../../store/form';
import { getEnabledDocuments } from '../../utility/randomUtil';
import NavigationBlocker from './NavigationBlocker';

import type { IAdditionalProps as IToolBarAdditional } from '../../components/Toolbar';
import type { TCreationPromiseType } from '../../store/documents/actions';
import type { INeuroDocument } from 'neuro-data-structures';
import type { Params } from 'react-router';

declare global {
  type StartEdit<Doc extends IDocumentV2 = IDocumentV2> = (
    /**  */
    document: Doc | Record<string, never>,
    /** When edit/add new button is pressed, name is used when document type differs from page name (eg. updrs_iii) */
    name?: Doc['_type'],
    /** used only when creating a new document (document._id not provided) */
    initialFormData?: SafeOmit<Doc, keyof IControlProps>,
  ) => (e?: React.MouseEvent) => void;
}

const getDocumentType = (id: string, documents: Array<{ _id: string; _type: string }>): string | undefined => {
  return (
    path(
      [0, '_type'],
      documents.filter(d => d._id === id),
    ) || undefined
  );
};

type Props = IStateFromProps &
  IDispatchFromProps &
  IOwnProps & {
    router: {
      navigate: any;
      location?: { state?: { relocate: string; initData?: Record<string, any> } };
      params: Readonly<Params<string>>;
    };
  };

/**
 * Handles loading toolbar and document form with necessary information and functions for forms
 */
class FormEditingHandler extends React.Component<Props, IOwnState> {
  private _isMounted = false;
  private _activeRequestController: AbortController | null = null;
  private _timer: NodeJS.Timeout | null = null;
  // This flag is set synchronously to prevent two click handlers firing in the same tick.
  // UI is driven by this.state.isBusy (updated asynchronously by setState).
  private _busy = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      formData: undefined,
      documentLoaded: false,
      editing: false,
      viewing: false,
      subDocuments: undefined,
      isBusy: false,
      busyAction: null,
    };
  }

  getDocument = (id: string) => this.props.documents.find((d: { _id: string }) => d._id === id);

  private cancelActiveRequest = () => {
    if (this._activeRequestController) {
      this._activeRequestController.abort();
      this._activeRequestController = null;
    }
  };

  // When edit/add new button is pressed, name is used when document type differs from page name (eg. updrs_iii)
  public startEdit: StartEdit = (document, name, initialFormData) => {
    return (): void => {
      // Guard against starting an edit while another operation is in progress
      if (this.state.editing || this.state.isBusy) {
        return;
      }

      if (document._id) {
        this.props
          .createCommit(
            {
              name: getDocumentType(document._id, this.props.documents) as string,
              id: document._id,
            },
            this.getDocument(document._id), // Current data
          )
          .then((commit: string | { _cid?: string }) => {
            if (!this._isMounted) return;

            if (document._id && typeof commit === 'object' && commit._cid) {
              this.loadFormValues(document._id);
              this.props.setEditing(getDocumentType(document._id, this.props.documents) as string, document._id);
            }
          })
          .catch(() => {
            return;
          });
      } else {
        this.props
          .createDocument(name || this.props.name, initialFormData)
          .then(() => {
            if (!this._isMounted) return;

            this.setState({ editing: true });
          })
          .catch(() => {
            return;
          });
      }
    };
  };

  // When view button is pressed
  public startView = (document: { _id?: string }) => (): void => {
    if (!this.state.viewing) {
      this.setState({ viewing: true });
      if (document._id) {
        this.props.setViewing(getDocumentType(document._id, this.props.documents) as string, document._id);
        this.loadFormValues(document._id);
      }
    }
  };

  // When view button is pressed
  public endView = (id: string) => (): void => {
    this.props.clearFormValues(id);
    this.props.unsetViewing(id);
    this.setState({ formData: undefined, documentLoaded: false, viewing: false });
  };

  // When save button is pressed
  public saveDraft = (id: string) => async (): Promise<void> => {
    if (this._busy) return;
    this._busy = true;

    if (this._timer) clearTimeout(this._timer);
    this.cancelActiveRequest();

    this.setState({ isBusy: true, busyAction: 'saving' });

    try {
      await this.props.closeCommit(
        { name: getDocumentType(id, this.props.documents) as string, id },
        undefined,
        this.state.subDocuments,
      );

      if (!this._isMounted) return;

      this.props.clearFormValues(id);
      this.props.unsetEditing(id);

      this.setState({
        formData: undefined,
        documentLoaded: false,
        editing: false,
        subDocuments: undefined,
      });
    } finally {
      // Release the synchronous lock regardless of mount state
      this._busy = false;
      // Ensure the busy state is always reset
      if (this._isMounted) {
        this.setState({ isBusy: false, busyAction: null });
      }
    }
  };

  // When cancel button is pressed
  public cancelDraft = (id: string) => async (): Promise<void> => {
    if (this._busy) return;
    this._busy = true;

    if (this._timer) clearTimeout(this._timer);
    this.cancelActiveRequest();

    this.setState({ isBusy: true, busyAction: 'cancelling' });

    try {
      await this.props.deleteCommit(
        { name: getDocumentType(id, this.props.documents) as string, id },
        this.state.subDocuments,
      );

      if (!this._isMounted) return;

      this.props.clearFormValues(id);
      this.props.unsetEditing(id);

      this.setState({
        formData: undefined,
        documentLoaded: false,
        editing: false,
        subDocuments: undefined,
      });
    } finally {
      // Release the synchronous lock regardless of mount state
      this._busy = false;
      // Ensure the busy state is always reset
      if (this._isMounted) {
        this.setState({ isBusy: false, busyAction: null });
      }
    }
  };

  public formValuesChange = (newValues: TOnChangeValues): void => {
    if (this._timer) {
      clearTimeout(this._timer);
    }

    this._timer = setTimeout(this.patchIfChanges, 3000);

    this.props.updateFormValues(this.props.editingDocument.id, newValues);
  };

  public patchIfChanges = async (): Promise<void> => {
    // Read the lock but don't set it, so a user save/cancel can abort a background patch
    if (this._busy || !this._isMounted) {
      return;
    }

    if (this.props.formData !== this.state.formData && this.props.editingDocument) {
      this.setState({ isBusy: true, busyAction: 'patching' });

      this.cancelActiveRequest();
      this._activeRequestController = new AbortController();

      try {
        await this.props.updateDocument(
          {
            name: getDocumentType(this.props.editingDocument.id, this.props.documents) as string,
            id: this.props.editingDocument.id,
          },
          this._activeRequestController.signal,
        );

        // This logic runs after a successful or aborted request
        if (this._isMounted) {
          this.setState({ formData: this.props.formData });
        }
      } finally {
        // Only reset if a save/cancel hasn't aborted this patch and taken over the busy state
        if (this._isMounted && this.state.busyAction === 'patching') {
          this._activeRequestController = null;
          this.setState({ isBusy: false, busyAction: null });
        }
      }
    }
  };

  public loadFormValues = (id: string): void => {
    if (this.props.documents && !this.state.documentLoaded) {
      const document = this.getDocument(id);
      // getDocument can miss when a route passes a filtered subset; loading undefined would throw
      if (!document) return;
      this.props.loadFormValues(document);
      this.setState({ documentLoaded: true });
    }
  };

  private checkRelocationState = (prevProps?: Props) => {
    const currentState = this.props.router.location?.state;
    const prevState = prevProps?.router.location?.state;

    if (currentState?.relocate) {
      // Trigger if:
      // 1. It's the initial mount (prevProps is undefined)
      // 2. OR the state object itself changed (a new navigate call happened)
      if (!prevProps || currentState !== prevState) {
        // 1. Navigate to the target route (to clear the state from the URL/history)
        this.props.router.navigate(`/${currentState.relocate}`, { replace: true, state: undefined });

        // 2. Trigger the edit mode
        this.startEdit({}, currentState.relocate as TDocumentType, currentState.initData)?.();
      }
    }
  };

  public componentDidMount = (): void => {
    this._isMounted = true;
    // When component is mounted, check if theres editing going on
    // This is needed especially if page is refreshed somewhere else and we need to load editing documents when this page is loaded
    if (this.props.documents && this.props.editingDocument) {
      this.setState({ editing: true });
      this.loadFormValues(this.props.editingDocument.id);
    } else if (this.props.router.location?.state?.relocate) {
      // Relocate to different page and create new document
      this.props.router.navigate(`/${this.props.router.location.state?.relocate}`, undefined);
      this.startEdit(
        {},
        this.props.router.location.state?.relocate as TDocumentType,
        this.props.router.location.state?.initData,
      );
    } else if (this.props.documents && this.props.viewingDocument) {
      this.setState({ viewing: true });
      this.loadFormValues(this.props.viewingDocument.id);
    }
    this.checkRelocationState();
  };

  public componentDidUpdate = (prevProps: Props): void => {
    // Check if editing id and documents appear (undefined => defined) when component is updated
    // This will load the documents when page is refreshed
    if (
      !this.state.editing &&
      (!prevProps.editingDocument || !prevProps.documents) &&
      this.props.documents &&
      this.props.editingDocument
    ) {
      this.setState({ editing: true });
      this.loadFormValues(this.props.editingDocument.id);
    }
    if (
      !this.state.viewing &&
      (!prevProps.viewingDocument || !prevProps.documents) &&
      this.props.documents &&
      this.props.viewingDocument
    ) {
      this.setState({ viewing: true });
      this.loadFormValues(this.props.viewingDocument.id);
    }
    this.checkRelocationState(prevProps);
  };

  public componentWillUnmount = (): void => {
    this._isMounted = false;

    if (this._timer) {
      clearTimeout(this._timer);
    }

    this.cancelActiveRequest();

    if (this.props.viewingDocument) {
      this.props.unsetViewing(this.props.viewingDocument.id);
    }
    // State cleanup
    this.setState({ formData: undefined, documentLoaded: false, subDocuments: undefined });
  };

  public setSubDocuments = (subDocuments: { name: string; id: string }[]): void => this.setState({ subDocuments });

  public render(): React.JSX.Element {
    const documentHasNewerSavedCommits = (): boolean | null => {
      const thisReduxDoc = this.props.reduxDocuments.find(rd => rd.documentId === this.props.editingDocument?.id);
      const myUnfinishedCommit = thisReduxDoc?.commits.find(c => c.creatorId === this.props.userId && !c.commitDate);
      if (!myUnfinishedCommit) return null;
      return thisReduxDoc?.commits.some(commits => (commits.commitDate || 0) >= myUnfinishedCommit.createDate) || false;
    };

    const disabledProps = documentHasNewerSavedCommits()
      ? { saveButtonDisabled: true, saveButtonDisabledTooltip: <FormattedMessage id="general.newerDateExists" /> }
      : {};

    return (
      <EditingLockedContent currentPage={this.props.name}>
        <Toolbar
          current={this.props.name}
          editing={this.props.editingDocument && this.props.editingDocument.id}
          saveDraft={this.saveDraft}
          cancelDraft={this.cancelDraft}
          view={{
            viewing: this.props.viewingDocument?.id,
            endView: this.endView,
          }}
          spinnerEnabled={this.state.busyAction === 'saving'}
          spinnerCancelButton={this.state.busyAction === 'cancelling'}
          patching={this.state.busyAction === 'patching'}
          {...{
            ...this.props.toolbarProps,
            saveButtonCallback: this.props.toolbarProps?.saveButtonCallback?.(
              this.props.editingDocument?.id
                ? ({
                    ...(this.props.formData[this.props.editingDocument.id] as object),
                    _id: this.props.editingDocument.id,
                  } as IDocumentV2)
                : undefined,
            ),
          }}
          {...disabledProps}
        />
        {this.props.editingDocument && !this.state.documentLoaded ? null : ( // Dont render form before the document has been loaded
          <NavigationBlocker blocked={!!this.props.editingDocument}>
            {this.props.children(
              this.props.editingDocument && this.props.editingDocument?.id,
              this.startEdit,
              {
                document: this.props.editingDocument?.id // If editing
                  ? path(['formData', this.props.editingDocument?.id], this.props) || {}
                  : this.props.viewingDocument?.id // If viewing
                    ? path(['formData', this.props.viewingDocument?.id], this.props) || {}
                    : {},
                onChange: this.props.editingDocument?.id
                  ? this.formValuesChange
                  : (): void => {
                      return;
                    },
              },
              {
                // Give children viewing document and tools
                viewing: this.props.viewingDocument?.id,
                // Document here is kinda useless as the viewing document is already injected into formData, but this is still used in some routes
                document: path(['formData', this.props.viewingDocument?.id], this.props) || {},
                startView: this.startView,
              },
              this.saveDraft,
              this.setSubDocuments,
            )}
          </NavigationBlocker>
        )}
      </EditingLockedContent>
    );
  }
}

interface IOwnState {
  formData?: TAnyObject;
  documentLoaded: boolean;
  editing: boolean;
  viewing: boolean;
  subDocuments?: { name: string; id: string }[];
  isBusy: boolean;
  busyAction: 'saving' | 'cancelling' | 'patching' | null;
}

interface IEditingDocument {
  id: string;
  name: string;
}

type TSubDocuments = { name: string; id: string }[];

interface IStateFromProps {
  editingDocument: IEditingDocument;
  viewingDocument: IEditingDocument;
  formData: TAnyObject;
  reduxDocuments: INeuroDocument[];
  userId: string;
}

interface IDispatchFromProps {
  clearFormValues: (id: string) => void;
  loadFormValues: (document: { _id: string }) => void;
  updateFormValues: (id: string, values: TOnChangeValues) => void;

  setEditing: (name: string, id: string) => void;
  unsetEditing: (id: string) => void;
  setViewing: (name: string, id: string) => void;
  unsetViewing: (id: string) => void;

  createDocument: (name: string, initialFormData?: Record<string, any>) => Promise<TCreationPromiseType>;
  createCommit: (editingDocument: IEditingDocument, oldData?: IControlProps) => Promise<TCreationPromiseType>;
  updateDocument: (editingDocument: IEditingDocument, signal?: AbortSignal) => Promise<TCreationPromiseType>;
  closeCommit: (
    editingDocument: IEditingDocument,
    formData?: TAnyObject,
    subDocuments?: TSubDocuments,
  ) => Promise<void>;
  deleteCommit: (editingDocument: IEditingDocument, subDocuments?: TSubDocuments) => Promise<void>;
}

// Return match name, unless theres subTypes then match all subTypes
const editingPredicate = (
  d: { name: string },
  props: { name: string },
  session: ISessionStore,
  document: IControlProps & any,
): boolean => {
  const enabledName = getEnabledDocuments(session).find(({ name }) => name === props.name);
  if (enabledName && enabledName.subTypes) {
    // Filter specific documents based on filtering function
    const subTypeFilter =
      enabledName.subTypesFiltering && enabledName.subTypesFiltering[d.name as TDocumentType]
        ? enabledName.subTypesFiltering[d.name as TDocumentType]?.(document)
        : true;
    return !!(enabledName.subTypes.includes(d.name as TDocumentType) && subTypeFilter);
  } else {
    return d.name === props.name;
  }
};

const mapStateToProps = (state: any, props: IOwnProps): IStateFromProps => {
  const sortedAndMergedDocument = (docID: string) =>
    (props.documents || []).find((doc: IControlProps) => doc._id === docID);
  return {
    editingDocument: state.form.editingDocuments.find((d: IEditingDocument) =>
      editingPredicate(d, props, state.session, sortedAndMergedDocument(d.id)),
    ),
    viewingDocument: state.form.viewingDocuments.find((d: IEditingDocument) =>
      editingPredicate(d, props, state.session, sortedAndMergedDocument(d.id)),
    ),
    formData: state.form.formData,
    reduxDocuments: state.documents.documents || [],
    userId: state.session.data?.useruuid || '',
  };
};

const mapDispatchToProps = (dispatch: any): any => ({
  clearFormValues: (id: string): void => dispatch(actionsForm.clearFormValues(id)),
  loadFormValues: (document: TAnyObject & IControlProps): void =>
    dispatch(actionsForm.checkForLoadFormValues(document)),
  updateFormValues: (id: string, values: TOnChangeValues): void => dispatch(actionsForm.updateFormValues(id, values)),

  setEditing: (name: string, id: string): void => dispatch(actionsForm.setEditing(name, id)),
  unsetEditing: (id: string): void => dispatch(actionsForm.unsetEditing(id)),
  setViewing: (name: string, id: string): void => dispatch(actionsForm.setViewing(name, id)),
  unsetViewing: (id: string): void => dispatch(actionsForm.unsetViewing(id)),

  createDocument: (name: string, initialFormData?: TAnyObject & IControlProps): Promise<TCreationPromiseType> =>
    dispatch(actionsDocument.createDocument(name, false, initialFormData)),
  createCommit: (
    editingDocument: IEditingDocument,
    formData: TAnyObject & IControlProps,
  ): Promise<TCreationPromiseType> => dispatch(actionsDocument.createCommit(editingDocument, formData)),
  updateDocument: (editingDocument: IEditingDocument, signal?: AbortSignal): Promise<TCreationPromiseType> =>
    dispatch(actionsDocument.updateDocumentData(editingDocument, undefined, signal)),
  closeCommit: (
    editingDocument: IEditingDocument,
    formData: TAnyObject & IControlProps,
    subDocuments?: TSubDocuments,
  ): Promise<void> => dispatch(actionsDocument.closeCommit(editingDocument, formData, subDocuments)),
  deleteCommit: (editingDocument: IEditingDocument, subDocuments?: TSubDocuments): void =>
    dispatch(actionsDocument.deleteCommit(editingDocument, subDocuments)),
});

export type ISaveDraft = InstanceType<typeof FormEditingHandler>['saveDraft'];

interface IOwnProps<T extends TDocumentType = TDocumentType> {
  name: string;
  documents: Array<IDocumentWithTypeV2<T>>;
  children: (
    editing: string,
    startEdit: StartEdit,
    formData: IFormData<IDocumentWithTypeV2<T>>,
    view?: {
      viewing: string;
      document: any;
      startView: (document: { _id?: string }, name?: string) => (e: React.MouseEvent) => void;
    },
    saveDraft?: (id: string) => () => Promise<void>,
    setSubDocuments?: (subDocuments: TSubDocuments) => void,
  ) => React.JSX.Element;
  toolbarProps?: { saveButtonCallback?: (document?: IDocumentV2) => () => void } & Omit<
    IToolBarAdditional,
    'saveButtonCallback'
  >;
}

/** withRouter replacement for react-router-dom */
const withRouter = (Component: any) => {
  const ComponentWithRouterProp = (props: IOwnProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    return <Component {...props} router={{ location, navigate, params }} />;
  };

  return ComponentWithRouterProp;
};

const FormEditingHandlerConnected = withRouter(
  connect<IStateFromProps, IDispatchFromProps, IOwnProps>(mapStateToProps, mapDispatchToProps)(FormEditingHandler),
);

/**
 * connect() and withRouter() both erase generics, so the document type parameter is reapplied
 * here. This is the single assertion that links formData to the document being edited: the
 * handler builds formData from that document's id, so the two always correspond at runtime.
 */
export default FormEditingHandlerConnected as unknown as <T extends TDocumentType>(
  props: IOwnProps<T>,
) => React.JSX.Element;
